import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import type { ResultOf } from '@graphql-typed-document-node/core';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';

import { EMPTY_ARRAY } from '@e-socna/empty';
import { invariant } from '@e-socna/utils';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { Id, FragmentType } from '@e-socna/data-layer';

import { NoData } from '../Illustrations/NoData';
import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import { ActorCard } from './Card';
import { NewActorCard } from './NewCard';
import { RulesCard } from './RulesCard';
import { orderedActorsOf } from './utils/actors-order.util';
import type { ActorsCmp_ActorableRules } from './fragments/actorable-rules.fragment';
import { AlertStack } from '../AlertStack';
import { ErrorsAlert } from '../ErrorsAlert';
import { NoticesAlert } from '../NoticesAlert';
import type { Notice } from '../NoticesAlert';

export const ActorsCmp_Actor_Fragment = graphql(`
  fragment ActorsCmp_Actor_Fragment on Actor {
    _id
    kind
    typeId
    isInternal
    categoryIds
    thirdPartyId
    thirdParty {
      _id
      ... on Organization {
        name
      }
      ... on Person {
        lastName
        firstName
      }
    }
    ... on ActorOrganization {
      useItsEdmDomain
    }
    ...ActorCardCmp_Actor_Fragment
    ...ActorForm_Actor_Fragment
  }
`);

export type ActorsCmp_Actor = ResultOf<typeof ActorsCmp_Actor_Fragment>;

interface Props {
  readonly actionsEnabled?: boolean;
  readonly actors:
    readonly FragmentType<typeof ActorsCmp_Actor_Fragment>[] | undefined | null;
  readonly consultThirdPartyInNewTab: boolean;
  readonly fallbackActors?:
    readonly FragmentType<typeof ActorsCmp_Actor_Fragment>[] | undefined | null;
  // False once the list holds all the actors it may hold.
  readonly isCreateEnabled?: boolean;
  // The list shown: the rules card tells its rules only.
  readonly isInternal: boolean;
  readonly onStartCreate?: () => void;
  readonly onStartCustomize?: () => void;
  readonly onStartFlush?: () => void;
  readonly onStartRemove?: (id: Id) => void;
  readonly onStartUpdate?: (id: Id) => void;
  readonly rules?: ActorsCmp_ActorableRules;
  // The refusals of the step showing the list.
  readonly errors?: readonly string[];
  // The information of the step showing the list, gathered with the inheritance one.
  readonly notices?: readonly Notice[];
  // The outcome of the last change of the list, shown until closed.
  readonly confirmation?: ReactNode;
}

export const Actors = ({
  actionsEnabled = false,
  actors: pActors,
  consultThirdPartyInNewTab,
  fallbackActors: pFallbackActors,
  isCreateEnabled = true,
  isInternal,
  onStartCreate: handleStartCreate,
  onStartCustomize: handleStartCustomize,
  onStartFlush: handleStartFlush,
  onStartRemove: handleStartRemove,
  onStartUpdate: handleStartUpdate,
  rules,
  errors = EMPTY_ARRAY,
  notices = EMPTY_ARRAY,
  confirmation,
}: Readonly<Props>) => {
  invariant(
    !actionsEnabled ||
      (!!handleStartCreate &&
        !!handleStartCustomize &&
        !!handleStartFlush &&
        !!handleStartRemove &&
        !!handleStartUpdate),
    'onStartCreate & onStartRemove & onStartUpdate & onStartFlush & onStartCustomize are required when actionsEnabled is true',
  );

  const actors = getFragmentData(ActorsCmp_Actor_Fragment, pActors);
  const fallbackActors = getFragmentData(
    ActorsCmp_Actor_Fragment,
    pFallbackActors,
  );

  const canInherit = !!fallbackActors;
  const mustInherit = canInherit && !actors;
  const inheritanceNotice: Notice | null = canInherit
    ? {
        message: mustInherit
          ? 'Ces acteurs sont pour le moment hérités.'
          : 'Ces acteurs peuvent être hérités.',
        action:
          actionsEnabled && handleStartCustomize && handleStartFlush ? (
            <Button
              color="inherit"
              onClick={mustInherit ? handleStartCustomize : handleStartFlush}
              size="small"
            >
              {mustInherit
                ? 'Spécifier les acteurs'
                : 'Hériter à nouveau des acteurs'}
            </Button>
          ) : undefined,
      }
    : null;
  const allNotices = inheritanceNotice
    ? [...notices, inheritanceNotice]
    : notices;
  const { actorCategoriesCodeMap } = useReferencesContext();
  const listedActors = mustInherit ? fallbackActors : actors;
  // The consultation orders the actors; the edition keeps the order they are given in.
  const actorsOrFallback =
    listedActors && !actionsEnabled
      ? orderedActorsOf(
          listedActors,
          isInternal
            ? actorCategoriesCodeMap.QuotationEmitter._id
            : actorCategoriesCodeMap.QuotationReceiver._id,
        )
      : listedActors;

  const [isRulesCardVisible, setIsRulesCardVisible] = useState(!!rules);
  const hideRulesCard = useCallback(() => {
    setIsRulesCardVisible(false);
  }, []);
  const showRulesCard = useCallback(() => {
    setIsRulesCardVisible(true);
  }, []);

  if (!actionsEnabled && !actorsOrFallback?.length) {
    return <NoData />;
  }

  return (
    <Box sx={{ pt: 2, width: '100%' }}>
      <AlertStack>
        {errors.length ? (
          <ErrorsAlert errors={errors.map((message) => ({ message }))} />
        ) : null}
        {allNotices.length ? <NoticesAlert notices={allNotices} /> : null}
        {confirmation}
      </AlertStack>
      <Grid container spacing={2}>
        {!actorsOrFallback?.length ? (
          <NoData />
        ) : (
          actorsOrFallback.map((actor) => (
            <ActorCard
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
              actionsEnabled={!!actionsEnabled && !mustInherit}
              actor={actor}
              consultThirdPartyInNewTab={consultThirdPartyInNewTab}
              key={actor._id}
              onStartRemove={handleStartRemove}
              onStartUpdate={handleStartUpdate}
            />
          ))
        )}
        {!!(actionsEnabled && handleStartCreate && isCreateEnabled) &&
          !mustInherit && (
            <NewActorCard
              isRulesCardVisible={isRulesCardVisible}
              onStartCreate={handleStartCreate}
              showRulesCard={showRulesCard}
            />
          )}
        {!!(rules && isRulesCardVisible) && !mustInherit && (
          <RulesCard
            isInternal={isInternal}
            onHideCard={hideRulesCard}
            rules={rules}
          />
        )}
      </Grid>
    </Box>
  );
};
